// QuickAddContactModal — "+ Add contact" from the personal CRM dashboard.
// Contacts are always project-scoped on the backend, so unlike the
// project-context ContactForm this asks which project the contact belongs
// to before creating it.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { createCrmContact as createCrmContactApi } from '../../services/api'
import { disambiguateProjectLabels } from '../../utils/projects'
import type { CrmContact, Project } from '../../types'

interface QuickAddContactModalProps {
  projects: Project[]
  onCreated: (contact: CrmContact) => void
  onClose: () => void
  /** Injectable for tests (defaults to the real API call) — avoids mocking
   * the services/api module entirely; tests pass a vi.fn() here instead. */
  createCrmContact?: typeof createCrmContactApi
}

export default function QuickAddContactModal({
  projects,
  onCreated,
  onClose,
  createCrmContact = createCrmContactApi,
}: QuickAddContactModalProps) {
  const labelledProjects = disambiguateProjectLabels(projects)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [orgName, setOrgName] = useState('')
  const [projectId, setProjectId] = useState(labelledProjects[0]?.project_id ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!firstName.trim() || !lastName.trim() || !projectId) return
    setSaving(true)
    setError('')
    try {
      const contact = await createCrmContact({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        organisation_name: orgName.trim(),
        project_id: projectId,
      })
      onCreated(contact)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">Add Contact</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600"><X size={18} /></button>
        </div>

        {labelledProjects.length === 0 ? (
          <div className="p-6 space-y-3">
            <p className="text-sm text-gray-600">
              Contacts belong to a project. Create a project first, then add contacts to it.
            </p>
            <Link
              to="/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600"
            >
              + New Disclosure
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First name *</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last name *</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Organisation</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Project *</label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                required
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
              >
                {labelledProjects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                type="submit"
                disabled={saving || !firstName.trim() || !lastName.trim() || !projectId}
                className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Create'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
